/**
 * ================================================================
 * CUSTOM HOOK: useIntegratedNotifications
 * ================================================================
 *
 * @hook             useIntegratedNotifications
 * @description      Unified hook managing both socket and Firebase notifications
 * @category         Business Logic Hook
 * ================================================================
 */

import { useCallback } from 'react';

import type { Notification } from '@/types/notifications';

import { useFirebaseNotifications } from './useFirebaseNotifications';
import { useNotifications } from './useNotifications';

interface IntegratedNotificationConfig {
  enableSocket?: boolean;
  enableFirebase?: boolean;
  autoRequestPermission?: boolean;
  pageSize?: number;
  onFirebaseMessage?: (payload: any) => void;
}

interface UseIntegratedNotificationsResult {
  // Socket notifications
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  activeTab: any;

  // Firebase
  isFirebaseReady: boolean;
  hasNotificationPermission: boolean;
  fcmToken: string | null;
  firebaseError: string | null;
  isFirebaseLoading: boolean;

  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  loadMore: () => Promise<void>;
  changeTab: (tab: any) => void;
  requestNotificationPermission: () => Promise<any>;
  refreshFCMToken: () => Promise<any>;
}

export function useIntegratedNotifications(
  uid: string,
  config: IntegratedNotificationConfig = {},
): UseIntegratedNotificationsResult {
  const { enableFirebase = true, pageSize = 10, onFirebaseMessage } = config;

  // Socket notifications
  const socketData = useNotifications(uid, {
    initialTab: 'All',
    pageSize,
  });

  // Firebase notifications
  const firebaseData = useFirebaseNotifications({
    config: {
      enableForegroundMessages: enableFirebase,
      enableBackgroundMessages: enableFirebase,
    },
    onForegroundMessage: (payload) => {
      console.log('🔥 Firebase message:', payload);
      if (onFirebaseMessage) {
        onFirebaseMessage(payload);
      }
    },
    autoRefresh: true,
  });

  // Actions
  const markAsRead = useCallback(
    async (notificationId: string) => {
      await socketData.markAsRead(notificationId);
      console.log('✅ Notification marked as read:', notificationId);
    },
    [socketData.markAsRead],
  );

  const markAllAsRead = useCallback(async () => {
    const unread = socketData.notifications.filter((n) => n.status === 'UNREAD');
    for (const notification of unread) {
      await markAsRead(notification.id);
    }
    console.log('✅ All notifications marked as read');
  }, [socketData.notifications, markAsRead]);

  const loadMore = useCallback(async () => {
    await socketData.loadMore();
    console.log('✅ More notifications loaded');
  }, [socketData.loadMore]);

  const changeTab = useCallback(
    (tab: any) => {
      socketData.changeTab(tab);
    },
    [socketData.changeTab],
  );

  const requestNotificationPermission = useCallback(async () => {
    if (enableFirebase && firebaseData.requestPermission) {
      const result = await firebaseData.requestPermission();
      console.log('🔔 Permission requested:', result);
      return result;
    } else {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        return {
          success: permission === 'granted',
          permission,
          requested: true,
        };
      }
      throw new Error('Notifications not supported');
    }
  }, [enableFirebase, firebaseData.requestPermission]);

  const refreshFCMToken = useCallback(async () => {
    if (enableFirebase && firebaseData.refreshToken) {
      const result = await firebaseData.refreshToken();
      console.log('🔄 FCM token refreshed:', result);
      return result;
    } else {
      console.warn('⚠️ Firebase not enabled');
    }
  }, [enableFirebase, firebaseData.refreshToken]);

  return {
    // Socket data
    notifications: socketData.notifications,
    unreadCount: socketData.unreadCount,
    isLoading: socketData.isLoading,
    hasMore: socketData.hasMore,
    activeTab: socketData.activeTab,

    // Firebase data
    isFirebaseReady: firebaseData.isInitialized,
    hasNotificationPermission: firebaseData.hasPermission,
    fcmToken: firebaseData.token,
    firebaseError: firebaseData.error,
    isFirebaseLoading: firebaseData.isLoading,

    // Actions
    markAsRead,
    markAllAsRead,
    loadMore,
    changeTab,
    requestNotificationPermission,
    refreshFCMToken,
  };
}
